/* eslint-disable max-lines -- This file contains a large legacy code */
import {
  AgencyDto,
  EmployeeDto,
  OnboardingPeriodWithEnrollmentStatusDto,
  OnboardingType,
  OpenEnrollmentPeriodDto,
  QualifyingLifeEventType,
} from '@zorro/clients';
import {
  getQualifyingLifeEventsDateRange,
  maximalDateISO,
  minimalDateISO,
} from '@zorro/common-utils';
import { Alert } from '@zorro/design-system/v2/Alert';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { DocumentCard } from '@zorro/design-system/v2/DocumentCard';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Select } from '@zorro/design-system/v2/Select';
import { Textarea } from '@zorro/design-system/v2/Textarea';
import { EvidenceFileIcon } from '@zorro/shared/assets';
import {
  formatDateEnLocale,
  getNow,
  parseDate,
  parseDateISO,
} from '@zorro/shared/formatters';
import { qualifyingLifeEventLabelConfig } from '@zorro/types';
import {
  Box,
  DateInput,
  DateRangeInput,
  LabelWithTooltip,
  Stack,
} from '@zorro/web/design-system';
import { FileUploader, FormFooter } from '@zorro/web/shared-ui';
import { downloadBenefitDocument, sortEnrollments } from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { Control, Controller, UseFormReturn, useWatch } from 'react-hook-form';

import { QleCoverageBanner } from './QleCoverageBanner';
import {
  ChangeEnrollmentsFormFields,
  addOrEditAlertBoxText,
  formFieldStyle,
  getAvailableEnrollmentTypes,
  handleFormStateChange,
  inlineGroupStyle,
  stackStyle,
} from './changeEnrollment.utils';

interface SharedEnrollmentFormProps {
  form: UseFormReturn<ChangeEnrollmentsFormFields>;
  onSubmit: (data: ChangeEnrollmentsFormFields) => Promise<void>;
  allOpenEnrollmentPeriods: OpenEnrollmentPeriodDto[];
  singleEmployeeDto?: EmployeeDto;
  associatedOtherOnboardingPeriodsSingleEmployee?: OnboardingPeriodWithEnrollmentStatusDto[];
  enrollmentTeams: AgencyDto[];
  monthlySEPDeadline: number | null; // All OEPs are for the same employer
  isMonthlySEPDeadlineEndOfMonth: boolean | null;
  shouldHideEnrollmentTeamFields?: boolean;
  hasLockedEnrollmentFields?: boolean;
}

export const ChangeEnrollmentForm = ({
  form,
  onSubmit,
  allOpenEnrollmentPeriods,
  singleEmployeeDto,
  associatedOtherOnboardingPeriodsSingleEmployee,
  enrollmentTeams,
  monthlySEPDeadline,
  isMonthlySEPDeadlineEndOfMonth,
  shouldHideEnrollmentTeamFields = false,
  hasLockedEnrollmentFields = false,
}: SharedEnrollmentFormProps) => {
  'use no memo';
  const { control, setValue, getValues, formState } = form;
  const { isValid } = formState;
  const isSingleEmployee = Boolean(singleEmployeeDto);
  const selectedOEPId = useWatch({
    control,
    name: 'planYear',
  });
  const selectedOEP = selectedOEPId
    ? allOpenEnrollmentPeriods?.find((oep) => oep.id === selectedOEPId)
    : undefined;

  const selectedEnrollmentType = useWatch({
    control,
    name: 'enrollmentType',
  });
  const isQleEnrollment = useWatch({
    control,
    name: 'isQleEnrollment',
  });
  const selectedQleType = useWatch({
    control,
    name: 'qle.type',
  });
  const selectedEventDate = useWatch({
    control,
    name: 'qle.occurredOn',
  });
  const uploadedQleEvidence = useWatch({
    control,
    name: 'qle.evidence',
  });
  const electionWindow = useWatch({
    control,
    name: 'electionWindow',
  });

  const formValues = {
    planYear: selectedOEP,
    enrollmentType: selectedEnrollmentType,
    isQleEnrollment,
    qleType: selectedQleType,
    qleEventDate: selectedEventDate,
    electionWindow,
  };

  const { minEventDate, maxEventDate } = getQualifyingLifeEventsDateRange({
    isOpsTool: true,
    enrollmentEffectiveFrom: selectedOEP?.effectiveFrom,
    enrollmentEffectiveUntil: selectedOEP?.effectiveUntil,
    eligibleFrom: singleEmployeeDto?.eligibleFrom,
    eligibleUntil: singleEmployeeDto?.eligibleUntil,
  });

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Submit"
        primaryButtonProps={{
          onClick: () => onSubmit(getValues()),
          disabled: !isValid,
        }}
        paperProps={{ pos: 'sticky' }}
      />
    ),
    [onSubmit, getValues, isValid]
  );
  useDrawerFooter(drawerFooter);

  return (
    <form>
      <Stack p="0 40px 0 40px" mih="calc(100vh - 198px)">
        <Alert>{addOrEditAlertBoxText(isSingleEmployee)}</Alert>
        <Box style={{ width: '100%' }}>
          <Box style={inlineGroupStyle}>
            <Controller
              control={control}
              name="planYear"
              render={({ field }) => (
                <Select
                  {...field}
                  required
                  label="Plan year"
                  data={allOpenEnrollmentPeriods
                    .sort(sortEnrollments)
                    .map((oep) => ({
                      label: parseDateISO(oep.effectiveFrom).year().toString(),
                      value: oep.id,
                    }))}
                  onChange={(value) => {
                    formValues.planYear =
                      allOpenEnrollmentPeriods.find(
                        (oep) => oep.id === value
                      ) || selectedOEP;
                    handleFormStateChange(
                      monthlySEPDeadline,
                      isMonthlySEPDeadlineEndOfMonth,
                      formValues,
                      'planYear',
                      setValue,
                      singleEmployeeDto,
                      associatedOtherOnboardingPeriodsSingleEmployee
                    );
                  }}
                  flex={1}
                  mb="1rem"
                  isReadOnly={hasLockedEnrollmentFields}
                />
              )}
            />
            <Controller
              control={control}
              name="enrollmentType"
              render={({ field: { onChange, ...rest } }) => (
                <Select
                  {...rest}
                  label="Enrollment type"
                  required
                  placeholder="Enrollment type"
                  data={getAvailableEnrollmentTypes(
                    selectedOEP,
                    singleEmployeeDto,
                    associatedOtherOnboardingPeriodsSingleEmployee
                  )}
                  onChange={(value) => {
                    formValues.enrollmentType = value as OnboardingType;
                    handleFormStateChange(
                      monthlySEPDeadline,
                      isMonthlySEPDeadlineEndOfMonth,
                      formValues,
                      'enrollmentType',
                      setValue,
                      singleEmployeeDto,
                      associatedOtherOnboardingPeriodsSingleEmployee
                    );
                  }}
                  disabled={!selectedOEP}
                  flex={1}
                  mb="1rem"
                />
              )}
            />
          </Box>
          {selectedEnrollmentType === OnboardingType.SPECIAL && (
            <Stack style={stackStyle}>
              <Box style={inlineGroupStyle}>
                <Controller
                  control={control}
                  name="isQleEnrollment"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <Checkbox
                      {...rest}
                      label={
                        <LabelWithTooltip
                          label="QLE enrollment"
                          tooltipLabel={
                            isSingleEmployee
                              ? ''
                              : 'Life events can only be reported for one employee at a time'
                          }
                        />
                      }
                      checked={value || false}
                      disabled={!isSingleEmployee}
                      onChange={({ currentTarget: { checked } }) => {
                        formValues.isQleEnrollment = checked;
                        handleFormStateChange(
                          monthlySEPDeadline,
                          isMonthlySEPDeadlineEndOfMonth,
                          formValues,
                          'isQleEnrollment',
                          setValue,
                          singleEmployeeDto,
                          associatedOtherOnboardingPeriodsSingleEmployee,
                          hasLockedEnrollmentFields
                        );
                      }}
                    />
                  )}
                />
              </Box>
              {isSingleEmployee && isQleEnrollment && (
                <Stack gap="8px">
                  <Box style={inlineGroupStyle}>
                    <Controller
                      control={control}
                      name="qle.type"
                      render={({ field: { onChange, ...rest } }) => (
                        <Select
                          {...rest}
                          label="Type of life event"
                          required
                          placeholder="Select life event"
                          data={Object.entries(
                            qualifyingLifeEventLabelConfig
                          ).map(([value, label]) => ({ label, value }))}
                          onChange={(value) => {
                            formValues.qleType =
                              value as QualifyingLifeEventType;
                            handleFormStateChange(
                              monthlySEPDeadline,
                              isMonthlySEPDeadlineEndOfMonth,
                              formValues,
                              'qle.type',
                              setValue,
                              singleEmployeeDto,
                              associatedOtherOnboardingPeriodsSingleEmployee,
                              hasLockedEnrollmentFields
                            );
                          }}
                          flex={1}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="qle.occurredOn"
                      render={({ field }) => (
                        <DateInput
                          {...field}
                          label="Event date"
                          placeholder="Select date"
                          required
                          defaultDate={minEventDate}
                          minDate={
                            minEventDate
                              ? parseDateISO(minEventDate).toDate()
                              : undefined
                          }
                          maxDate={
                            maxEventDate
                              ? parseDateISO(maxEventDate).toDate()
                              : undefined
                          }
                          onChange={(value) => {
                            formValues.qleEventDate = value as Date;
                            handleFormStateChange(
                              monthlySEPDeadline,
                              isMonthlySEPDeadlineEndOfMonth,
                              formValues,
                              'qle.occurredOn',
                              setValue,
                              singleEmployeeDto,
                              associatedOtherOnboardingPeriodsSingleEmployee,
                              hasLockedEnrollmentFields
                            );
                          }}
                          flex={1}
                        />
                      )}
                    />
                  </Box>
                  {selectedQleType === QualifyingLifeEventType.OTHER && (
                    <Controller
                      control={control}
                      name="qle.otherReason"
                      render={({ field: { ref: _ref, value, ...rest } }) => (
                        <Textarea
                          {...rest}
                          placeholder="Description. Max 100 characters"
                          value={value || ''}
                          maxLength={100}
                          minRows={3}
                          autosize
                          flex={1}
                          mb="1rem"
                        />
                      )}
                    />
                  )}
                  <Stack style={stackStyle}>
                    <FileUploader
                      control={control as unknown as Control}
                      name="qle.evidence"
                      label="Life event evidence"
                      isLoading={false}
                      onDrop={(files: File[]) => {
                        const uploaded = [...(uploadedQleEvidence ?? [])];
                        uploaded.push(...files);
                        setValue('qle.evidence', uploaded);
                      }}
                      buttonText="Upload evidence"
                      zoneLabelText="Drag file here or click to select it"
                      isMultiple
                    >
                      <EvidenceFileIcon />
                    </FileUploader>
                    {Array.isArray(uploadedQleEvidence) &&
                      uploadedQleEvidence.map((uploaded) => {
                        const handleRemove = () => {
                          const updated = uploadedQleEvidence.filter(
                            (item) => item !== uploaded
                          );
                          setValue('qle.evidence', updated);
                        };

                        if (uploaded instanceof File) {
                          return (
                            <DocumentCard
                              key={uploaded.name}
                              fileName={uploaded.name}
                              date={formatDateEnLocale(getNow())}
                              onDelete={handleRemove}
                            />
                          );
                        }

                        return (
                          <DocumentCard
                            key={uploaded.fileName}
                            fileName={uploaded.fileName}
                            date={formatDateEnLocale(uploaded.createdAt)}
                            onDownload={() =>
                              downloadBenefitDocument(
                                uploaded.documentUrl,
                                uploaded.fileName
                              )
                            }
                            onDelete={handleRemove}
                          />
                        );
                      })}
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}
          <Box style={inlineGroupStyle}>
            <Controller
              control={control}
              name="electionWindow"
              render={({ field: { onChange, ...rest } }) => (
                <DateRangeInput
                  {...rest}
                  required
                  label="Election window"
                  maxDate={
                    selectedEnrollmentType === OnboardingType.OPEN_ENROLLMENT &&
                    selectedOEP
                      ? parseDateISO(selectedOEP.effectiveFrom)
                          .subtract(1, 'day')
                          .toDate()
                      : undefined
                  }
                  onChange={(value) => {
                    const [newFrom, newUntil] = value;
                    if (newFrom && newUntil) {
                      formValues.electionWindow = [newFrom, newUntil];
                      handleFormStateChange(
                        monthlySEPDeadline,
                        isMonthlySEPDeadlineEndOfMonth,
                        formValues,
                        'electionWindow',
                        setValue,
                        singleEmployeeDto,
                        associatedOtherOnboardingPeriodsSingleEmployee
                      );
                    }
                    onChange(value);
                  }}
                  style={formFieldStyle}
                  disabled={
                    !selectedOEP ||
                    !selectedEnrollmentType ||
                    (isQleEnrollment &&
                      (!selectedQleType || !selectedEventDate)) ||
                    hasLockedEnrollmentFields
                  }
                />
              )}
            />
            {!hasLockedEnrollmentFields && (
              <Controller
                control={control}
                name="expectedStartOfCoverage"
                render={({ field }) => (
                  <DateInput
                    {...field}
                    required
                    label="Expected start of coverage"
                    style={formFieldStyle}
                    minDate={
                      isSingleEmployee
                        ? selectedEnrollmentType === OnboardingType.SPECIAL &&
                          selectedOEP?.effectiveFrom &&
                          electionWindow?.length
                          ? parseDateISO(
                              maximalDateISO(
                                isQleEnrollment
                                  ? electionWindow[0]
                                  : parseDate(electionWindow[1]).add(1, 'day'),
                                selectedOEP?.effectiveFrom,
                                singleEmployeeDto?.eligibleFrom
                              )
                            ).toDate()
                          : undefined
                        : electionWindow?.[1]
                          ? parseDateISO(
                              maximalDateISO(
                                electionWindow[1],
                                selectedOEP?.effectiveFrom
                              )
                            ).toDate()
                          : undefined
                    }
                    maxDate={
                      selectedOEP?.effectiveUntil
                        ? selectedEnrollmentType === OnboardingType.SPECIAL
                          ? parseDateISO(
                              minimalDateISO(
                                selectedOEP.effectiveUntil,
                                singleEmployeeDto?.eligibleUntil
                              )
                            ).toDate()
                          : parseDateISO(selectedOEP.effectiveUntil).toDate()
                        : undefined
                    }
                    disabled={
                      !selectedOEP ||
                      !selectedEnrollmentType ||
                      (isQleEnrollment &&
                        (!selectedQleType || !selectedEventDate))
                    }
                    isReadOnly={
                      selectedEnrollmentType === OnboardingType.OPEN_ENROLLMENT
                    }
                  />
                )}
              />
            )}
          </Box>
          {isSingleEmployee && isQleEnrollment && (
            <Box mb="md">
              <QleCoverageBanner
                qleType={selectedQleType}
                eventDate={selectedEventDate}
              />
            </Box>
          )}
          {!shouldHideEnrollmentTeamFields && (
            <Box style={{ width: '100%' }}>
              <Controller
                control={control}
                name="enrollmentTeamId"
                render={({ field: { onChange, ...rest } }) => (
                  <Select
                    {...rest}
                    searchable
                    label="Enrollment team"
                    placeholder="Select an enrollment team"
                    data={enrollmentTeams.map((team) => ({
                      label: team.name,
                      value: team.id,
                    }))}
                    onChange={(value) => {
                      onChange(value);
                      setValue('enrollmentAgentId', undefined);
                    }}
                    flex={1}
                    mb="1rem"
                    isReadOnly={enrollmentTeams.length === 1}
                  />
                )}
              />
            </Box>
          )}
        </Box>
      </Stack>
    </form>
  );
};
